import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  updateManagedUser,
  uploadUserAttachment,
  downloadUsersManage_,
} from "../utils/api.js";
import {
  loadManagedUsers,
  peekManagedUsers,
  syncManagedUsers,
  loadAbnStatusMap,
  peekAbnStatusMap,
  loadVevoStatusMap,
  peekVevoStatusMap,
} from "../utils/usersCache.js";
import { ErrorIcon } from "../components/shared/Icons";
import ComplianceExpiryModal from "../components/ComplianceExpiryModal";
import ContractExpiryModal from "../components/ContractExpiryModal";
import ABNLookup from "../components/ABNLookup";
import VEVOInfo from "../components/VEVOInfo";
import LoadingState from "../components/shared/LoadingState";
import SearchInput from "../components/shared/SearchInput";
import CustomSelect from "../components/shared/CustomSelect";
import HelpButton from "../components/shared/HelpButton";
import ContractViewer from "../components/ContractViewer";
import { cssUrl } from "../utils/cssVersion";

const personalStyles = cssUrl("newuseronboarding.css");

/* ============================================================================
 * Two pages, one component
 * ----------------------------------------------------------------------------
 *   <UsersManagement />                       variant="basic"
 *     Help + Contractor/Employee Excel downloads. Loads only the records.
 *
 *   <UsersManagement variant="compliance" />  (see UsersCompliance.jsx)
 *     Help + Compliance Expiry / Contract Expiry / ABN Status / VEVO Info
 *     icons with alert badges. Also runs the ABN lookup and VEVO scan.
 *
 * Both share the search card, Working/Contractor toggles, info card and the
 * Info Data / Contract Data panels. Records come from the page-level
 * usersCache (paint from cache, revalidate in the background).
 * ==========================================================================*/

/* ============================================================================
 * Field metadata
 * ==========================================================================*/

// Work Status substitution: ABN -> Contractor, TFN -> Employee
const STATUS_DISPLAY = { ABN: "Contractor", TFN: "Employee" };
const statusDisplay = (ws) => STATUS_DISPLAY[ws] ?? ws ?? "—";

const FIELD_LABELS = {
  name: "Full Name",
  photo: "Photo",
  workstatus: "Status",
  firstName: "First Name",
  surname: "Surname",
  fullLegalName: "Full Legal Name",
  banned: "Banned",
  email: "Email",
  mobile: "Mobile",
  current: "Current",
  working: "Working",
  dateOfBirth: "Date Of Birth",
  startWork: "Start Work",
  workingWithChildren: "Working With Children",
  workChildrenExpiry: "Work Children Expiry",
  policeCheckIssuedDate: "Police Check Issued Date",
  visaType: "Visa Type",
  visaExpiry: "Visa Expiry",
  typeOfEmployment: "Type Of Employment",
  businessName: "Business Name",
  abnNumber: "ABN Number",
  tfnNumber: "TFN Number",
  address: "Address",
  superFundName: "Super Fund Name",
  superFundAccountName: "Super Fund Account Name",
  membershipNumber: "Membership Number",
  bankAccountName: "Bank Account Name",
  bsbNumber: "BSB Number",
  accountNumber: "Account Number",
  visaStatus: "Visa Status",
  childrenStatus: "Children Status",
  currentlyWorkingWeekdays: "Currently Working Weekdays",
  activeWeekdaysSince: "Active Weekdays Since",
  snlContractStatus: "SNL Contract Status",
  snlRefuseStatus: "SNL Refuse Status",
  policeStatus: "Police Status",
  uploadPoliceCheck: "Upload Police Check",
  visa: "Visa",
  snlContract: "SNL Contract",
  refuseTfnContract: "Refuse TFN Contract",
  hrDocuments: "HR Documents",
  passport: "Passport",
  workingChildrenCard: "Working Children Card",
  policeExpiry: "Police Expiry",
  police3MonthsPrior: "Police 3 Months Prior",
  visa3MonthsPrior: "Visa 3 Months Prior",
  children3MonthsPrior: "Children 3 Months Prior",
  contractExpiry: "Contract Expiry",
  refuseTfnExpiry: "Refuse TFN Expiry",
};

// Label substitution mirroring Status:
// ABN Number -> Contractor Number, TFN Number -> Employee Number
const labelFor = (field) => {
  if (field === "abnNumber") return "Contractor Number";
  if (field === "tfnNumber") return "Employee Number";
  return FIELD_LABELS[field] ?? field;
};

const DATE_FIELDS = new Set([
  "dateOfBirth",
  "startWork",
  "workChildrenExpiry",
  "policeCheckIssuedDate",
  "visaExpiry",
]);

const CURRENT_OPTIONS = ["Active", "Non Active"];
const WORKING_OPTIONS = ["Working", "Not Working"];

// Dropdown fields rendered with CustomSelect (auto-save on change)
const SELECT_OPTIONS = {
  current: CURRENT_OPTIONS,
  working: WORKING_OPTIONS,
  workingWithChildren: ["Yes", "No"],
  visaType: [
    "Citizen",
    "Permanent Residency",
    "Student Visa",
    "Bridging Visa",
    "Family & Partner Visa",
    "Visitor Visa",
    "Trade Visa",
    "Refugee Visa",
    "Other",
    "Temporary Graduate",
    "Temporary Activity",
  ],
};

// Read-only status fields shown in the Info Data panel
// Shown in the Info Data panel's "Status (read-only)" section
const READONLY_INFO_FIELDS = [
  "typeOfEmployment",
  "visaStatus",
  "childrenStatus",
  "snlContractStatus",
  "snlRefuseStatus",
  "policeStatus",
];

// ALL fields that must render read-only, wherever they appear
const READONLY_SET = new Set([
  ...READONLY_INFO_FIELDS,
  "policeExpiry",
  "police3MonthsPrior",
  "visa3MonthsPrior",
  "children3MonthsPrior",
  "contractExpiry",
  "refuseTfnExpiry",
]);

// Info Data panel — the ABN/TFN number field depends on the record type
const infoFieldOrder = (workstatus) => [
  "firstName",
  "surname",
  "fullLegalName",
  "banned",
  "mobile",
  "address",
  "startWork",
  "dateOfBirth",
  workstatus === "TFN" ? "tfnNumber" : "abnNumber",
  "superFundName",
  "superFundAccountName",
  "membershipNumber",
];

const BANKING_FIELD_ORDER = ["bankAccountName", "bsbNumber", "accountNumber"];

// Contract Data panel
const CONTRACT_SCALAR_ORDER = [
  "policeExpiry",
  "visaExpiry",
  "workChildrenExpiry",
  "police3MonthsPrior",
  "visa3MonthsPrior",
  "children3MonthsPrior",
  "workingWithChildren",
  "visaType",
  "working",
  "policeCheckIssuedDate",
  "contractExpiry",
  "refuseTfnExpiry",
];
const ATTACHMENT_FIELD_ORDER = [
  "uploadPoliceCheck",
  "visa",
  "snlContract",
  "refuseTfnContract",
  "hrDocuments",
  "passport",
  "workingChildrenCard",
];

// Read-only date fields (formula outputs) shown as DD/MM/YYYY
const READONLY_DATE_DISPLAY = new Set([
  "policeExpiry",
  "police3MonthsPrior",
  "visa3MonthsPrior",
  "children3MonthsPrior",
  "contractExpiry",
  "refuseTfnExpiry",
]);

// "2026-07-08" or "2026-07-08T00:00:00.000Z" -> "08/07/2026"
const toDisplayDate = (v) => {
  const s = (v ?? "").toString().trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
};

/* ============================================================================
 * Format rules — applied while typing AND validated on blur
 * ==========================================================================*/

const digitsOnly = (s) => (s ?? "").toString().replace(/\D/g, "");
const stripSpaces = (s) => (s ?? "").toString().replace(/\s+/g, "");

// (042) 477-2323 — max 14 characters
const formatMobile = (s) => {
  const d = digitsOnly(s).slice(0, 10);
  if (d.length === 0) return "";
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

// 063-019 — max 7 characters
const formatBsb = (s) => {
  const d = digitsOnly(s).slice(0, 6);
  return d.length <= 3 ? d : `${d.slice(0, 3)}-${d.slice(3)}`;
};

const FIELD_FORMATTERS = {
  mobile: formatMobile,
  bsbNumber: formatBsb,
  abnNumber: stripSpaces,
  tfnNumber: stripSpaces,
  accountNumber: stripSpaces,
};

const FIELD_MAXLENGTH = { mobile: 14, bsbNumber: 7 };

const FIELD_VALIDATORS = {
  mobile: (v) =>
    /^\(\d{3}\) \d{3}-\d{4}$/.test(v) ||
    "Mobile must be in the format (042) 477-2323",
  bsbNumber: (v) =>
    /^\d{3}-\d{3}$/.test(v) || "BSB must be in the format 063-019",
};

/* ============================================================================
 * Utilities
 * ==========================================================================*/

const normalizeName = (name) =>
  (name ?? "").toString().toLowerCase().replace(/\s+/g, "");

const parseAttachments = (raw) => {
  if (!raw) return [];
  const str = raw.toString().trim();
  if (!str) return [];
  if (str.startsWith("[")) {
    try {
      const parsed = JSON.parse(str);
      if (Array.isArray(parsed)) return parsed.filter((i) => i && i.url);
    } catch {
      /* fall through */
    }
  }
  return [{ type: "img", url: str }];
};

/* ============================================================================
 * Image viewer modal
 * ==========================================================================*/

const ImageViewer = ({ url, onClose }) => {
  useEffect(() => {
    const handleKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const isMobile = window.innerWidth < 768;

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, top: "var(--fr-modal-top, 0px)", background: "rgba(0,0,0,0.85)", zIndex: 999 }}
      />
      <div
        style={{
          position: "fixed",
          top: "calc(var(--fr-modal-top, 0px) + (100dvh - var(--fr-modal-top, 0px) - 68px) / 2)",   // centred UNDER the info bar
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: isMobile ? "95vw" : "90vw",
          height: isMobile ? "min(85vh, calc(100dvh - var(--fr-modal-top, 0px) - 108px))" : "min(90vh, calc(100dvh - var(--fr-modal-top, 0px) - 108px))",
          background: "#1a1a1a",
          borderRadius: "10px",
          overflow: "hidden",
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "8px 12px",
            background: "#777B7E",
            borderRadius: "10px 10px 0 0",
            flexShrink: 0,
          }}
        >
          <span style={{ color: "#fff", fontSize: "0.85rem", opacity: 0.85 }}>
            Image Viewer
          </span>
          <button
            onClick={onClose}
            aria-label="Close image viewer"
            style={{
              background: "#dc2626",
              border: "1px solid rgba(239,68,68,0.3)",
              color: "#fff",
              borderRadius: "50%",
              width: "30px",
              height: "30px",
              fontSize: "16px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ✕
          </button>
        </div>
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
            overflow: "auto",
            background: "#0f0f0f",
          }}
        >
          <img
            src={url}
            alt="Attachment"
            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: "4px" }}
          />
        </div>
      </div>
    </>
  );
};

/* ============================================================================
 * Saving wrapper — blurs + locks a field while its save is in flight
 * ==========================================================================*/

const SavingWrap = ({ saving, children, style }) => (
  <div
    style={{
      minWidth: 0,
      filter: saving ? "blur(2px)" : "none",
      opacity: saving ? 0.5 : 1,
      pointerEvents: saving ? "none" : "auto",
      transition: "filter 0.15s ease, opacity 0.15s ease",
      ...style,
    }}
  >
    {children}
  </div>
);

/* ============================================================================
 * EditableText — trims, formats while typing, validates + auto-saves on blur
 * ==========================================================================*/

const EditableText = ({ record, field, saving, onSave, onValidationError }) => {
  const [value, setValue] = useState(record[field] ?? "");
  const isDate = DATE_FIELDS.has(field);
  const formatter = FIELD_FORMATTERS[field];

  useEffect(() => {
    setValue(record[field] ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record.recordId, record[field]]);

  const handleChange = (e) => {
    const raw = e.target.value;
    setValue(formatter ? formatter(raw) : raw);
  };

  const handleBlur = () => {
    let next = (value ?? "").toString().trim();
    if (formatter) next = formatter(next);

    const prev = (record[field] ?? "").toString().trim();
    if (next === prev) {
      setValue(prev);
      return;
    }
    if (!next) {
      setValue(record[field] ?? "");
      onValidationError(`${labelFor(field)} is required and was not saved.`);
      return;
    }
    const validate = FIELD_VALIDATORS[field];
    if (validate) {
      const result = validate(next);
      if (result !== true) {
        setValue(record[field] ?? "");
        onValidationError(result);
        return;
      }
    }
    onSave(record, field, next);
  };

  return (
    <SavingWrap saving={saving} style={{ display: "flex", flexDirection: "column" }}>
      <div className="ob-field">
        <label className="ob-label">{labelFor(field)}</label>
        <input
          type={isDate ? "date" : "text"}
          value={value}
          maxLength={FIELD_MAXLENGTH[field]}
          onChange={handleChange}
          onBlur={handleBlur}
          className="ob-input"
          placeholder={field === "mobile" ? "(042) 477-2323" : field === "bsbNumber" ? "063-019" : ""}
        />
      </div>
    </SavingWrap>
  );
};

/* ============================================================================
 * AttachmentCarousel — carousel + Add button (appends to existing files)
 * ==========================================================================*/

const AttachmentCarousel = ({ record, field, busy, onAddFile }) => {
  const items = parseAttachments(record[field]);
  const [index, setIndex] = useState(0);
  const [viewerOpen, setViewerOpen] = useState(false);
  const fileInputRef = useRef(null);

  const total = items.length;
  const safeIndex = total === 0 ? 0 : Math.min(index, total - 1);
  const current = total > 0 ? items[safeIndex] : null;
  const isImage = current?.type === "img";
  const fileName = current?.filename || `File ${safeIndex + 1}`;

  const prev = () => setIndex((i) => (i - 1 + total) % total);
  const next = () => setIndex((i) => (i + 1) % total);

  const handleFilePicked = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) onAddFile(record, field, file);
  };

  const arrowStyle = {
    flexShrink: 0,
    width: "32px",
    height: "32px",
    borderRadius: "8px",
    border: "1px solid rgba(34, 211, 238, 0.2)",
    background: "rgba(34, 211, 238, 0.08)",
    color: "#22d3ee",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  return (
    <SavingWrap saving={busy}>
      <div className="ob-field">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "6px",
          }}
        >
          <label className="ob-label" style={{ marginBottom: 0 }}>
            {labelFor(field)}
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {total > 1 && (
              <span
                style={{
                  fontSize: "0.7rem",
                  color: "rgba(34, 211, 238, 0.7)",
                  fontWeight: 600,
                }}
              >
                {safeIndex + 1} of {total}
              </span>
            )}
            <button
              onClick={() => fileInputRef.current?.click()}
              title={`Add a PDF to ${labelFor(field)}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                padding: "3px 10px",
                borderRadius: "999px",
                border: "1px solid rgba(34, 197, 94, 0.35)",
                background: "rgba(34, 197, 94, 0.12)",
                color: "#4ade80",
                fontSize: "0.72rem",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
              </svg>
              Add
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,image/*"
              style={{ display: "none" }}
              onChange={handleFilePicked}
            />
          </div>
        </div>

        {total === 0 ? (
          <div
            style={{
              padding: "12px 14px",
              background: "rgba(255, 255, 255, 0.03)",
              border: "1px dashed rgba(255, 255, 255, 0.12)",
              borderRadius: "10px",
              color: "rgba(255, 255, 255, 0.4)",
              fontSize: "0.85rem",
              fontStyle: "italic",
            }}
          >
            No documents uploaded — use Add to attach one.
          </div>
        ) : (
          <div
            style={{
              background: "rgba(255, 255, 255, 0.03)",
              border: "1px solid rgba(34, 211, 238, 0.15)",
              borderRadius: "10px",
              padding: "12px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              minWidth: 0,
            }}
          >
            {total > 1 && (
              <button onClick={prev} aria-label="Previous attachment" style={arrowStyle}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}

            <div style={{ flex: 1, display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
              {isImage ? (
                <button
                  onClick={() => setViewerOpen(true)}
                  title="Click to view image"
                  style={{
                    width: "64px",
                    height: "64px",
                    borderRadius: "8px",
                    flexShrink: 0,
                    border: "1px solid rgba(255, 255, 255, 0.08)",
                    padding: 0,
                    cursor: "pointer",
                    background: "transparent",
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={current.url}
                    alt={fileName}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                </button>
              ) : (
                <button
                  onClick={() => setViewerOpen(true)}
                  title="Click to view PDF"
                  style={{
                    width: "64px",
                    height: "64px",
                    borderRadius: "8px",
                    background:
                      "linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.1) 100%)",
                    border: "1px solid rgba(239, 68, 68, 0.25)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#f87171",
                    flexShrink: 0,
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  PDF
                </button>
              )}

              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: 500,
                    color: "#e2e8f0",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={fileName}
                >
                  {fileName}
                </div>
                <button
                  onClick={() => setViewerOpen(true)}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    marginTop: "4px",
                    fontSize: "0.75rem",
                    color: "#22d3ee",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {isImage ? "View image" : "View PDF"}
                </button>
              </div>
            </div>

            {total > 1 && (
              <button onClick={next} aria-label="Next attachment" style={arrowStyle}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>
        )}

        {viewerOpen && current && (
          isImage ? (
            <ImageViewer url={current.url} onClose={() => setViewerOpen(false)} />
          ) : (
            <>
              <div
                onClick={() => setViewerOpen(false)}
                style={{ position: "fixed", inset: 0, top: "var(--fr-modal-top, 0px)", background: "rgba(0,0,0,0.6)", zIndex: 999 }}
              />
              <ContractViewer pdfUrl={current.url} onClose={() => setViewerOpen(false)} />
            </>
          )
        )}
      </div>
    </SavingWrap>
  );
};

/* ============================================================================
 * Header icons
 * ==========================================================================*/

// Download-tray icon with a small role letter badge ("C" or "E")
const ExportIcon = ({ letter }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 4v9m0 0l-3.5-3.5M12 13l3.5-3.5M4 17v1a2 2 0 002 2h12a2 2 0 002-2v-1"
    />
    <text
      x="19"
      y="9"
      fontSize="9"
      fontWeight="700"
      fill="currentColor"
      stroke="none"
      textAnchor="middle"
    >
      {letter}
    </text>
  </svg>
);

const ClockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="9" />
    <polyline strokeLinecap="round" strokeLinejoin="round" points="12 7 12 12 15.5 14" />
  </svg>
);

const ContractClockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h6" />
    <polyline strokeLinecap="round" strokeLinejoin="round" points="14 3 14 8 19 8" />
    <circle cx="17" cy="17" r="5" />
    <polyline strokeLinecap="round" strokeLinejoin="round" points="17 15 17 17 18.8 18" />
  </svg>
);

const AbnIcon = () => (          // ID-badge / business number icon
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 9h4M7 13h6M7 17h3" />
    <circle cx="16.5" cy="9" r="1.5" />
  </svg>
);

const VevoIcon = () => (          // passport / travel-document icon
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="4" y="3" width="16" height="18" rx="2" />
    <circle cx="12" cy="9" r="2.5" />
    <path strokeLinecap="round" d="M8 15h8M9 17.5h6" />
  </svg>
);

const ClockIconButton = ({ onClick, alertCount, title = "Compliance Expiry's", icon = <ClockIcon /> }) => (
  <button
    onClick={onClick} title={title} aria-label={title}
    style={{
      position: "relative",
      width: "32px",
      height: "32px",
      borderRadius: "8px",
      border: "1px solid rgba(34, 211, 238, 0.25)",
      background: "rgba(34, 211, 238, 0.08)",
      color: "#22d3ee",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 0,
      flexShrink: 0,
    }}
  >
    {icon}
    {alertCount > 0 && (
      <span
        style={{
          position: "absolute",
          top: "-5px",
          right: "-5px",
          minWidth: "16px",
          height: "16px",
          borderRadius: "999px",
          background: "#dc2626",
          color: "#fff",
          fontSize: "0.6rem",
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "0 4px",
        }}
      >
        {alertCount}
      </span>
    )}
  </button>
);

const DownloadIconButton = ({ title, filename, fetchFile, arg, setToast }) => {
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const blob = await fetchFile(arg);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setToast({ type: "success", message: `${title} downloaded.` });
    } catch (err) {
      console.error("Download failed:", err);
      setToast({ type: "error", message: err.message || "Download failed." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={busy}
      title={title}
      aria-label={title}
      style={{
        width: "32px",
        height: "32px",
        borderRadius: "8px",
        border: "1px solid rgba(34, 211, 238, 0.25)",
        background: "rgba(34, 211, 238, 0.08)",
        color: "#22d3ee",
        cursor: busy ? "wait" : "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
        opacity: busy ? 0.5 : 1,
        transition: "opacity 0.15s ease",
        flexShrink: 0,
      }}
    >
      <ExportIcon letter={title.startsWith("Contractor") ? "C" : "E"} />
    </button>
  );
};

/* ============================================================================
 * Main component
 * ==========================================================================*/

const THREE_COL_GRID = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  columnGap: "20px",
  rowGap: "12px",
};

// Per-variant header copy + help PDF
const VARIANT_META = {
  basic: {
    title: "User Management",
    helper:
      "Search for a user, flip between Working and Not Working, then open Info Data or Contract Data. Edited fields save automatically when you leave them.",
    help: { chip: "usersmanagement", urlFilter: "/static/pdffilters/UsersManagement.pdf" },
  },
  compliance: {
    title: "User Compliance",
    helper:
      "Use the icons to review expiring compliance checks, contracts, ABN status and visas. Click a record in any list to open it here for editing.",
    help: { chip: "userscompliance", urlFilter: "/static/pdffilters/UsersCompliance.pdf" },
  },
};

const UsersManagement = ({ variant = "basic" }) => {
  const isCompliance = variant === "compliance";
  const meta = VARIANT_META[variant] ?? VARIANT_META.basic;

  // Records are seeded from the page-level cache so a warm page paints
  // instantly; the initial-fetch effect then revalidates in the background.
  const [records, setRecords] = useState(() => peekManagedUsers() ?? []);
  const [loading, setLoading] = useState(() => !peekManagedUsers());
  const [loadError, setLoadError] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedName, setSelectedName] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [workingFilter, setWorkingFilter] = useState("Working");
  const [roleFilter, setRoleFilter] = useState(null);
  const [activePanel, setActivePanel] = useState(null); // 'info' | 'contract' | null

  const [busyFields, setBusyFields] = useState(new Set());
  const [toast, setToast] = useState(null);

  const searchWrapperRef = useRef(null);

  // ---- compliance-only state (never populated on the basic page) ----
  const [expiryOpen, setExpiryOpen] = useState(false);
  const [contractExpiryOpen, setContractExpiryOpen] = useState(false);

  // ABN status (ABR lookup) — cached across visits, fetched in the background
  const [abnOpen, setAbnOpen] = useState(false);
  const [abnStatuses, setAbnStatuses] = useState(() => peekAbnStatusMap() ?? {}); // recordId -> { status, gst, entityName }
  const [abnLoading, setAbnLoading] = useState(false);

  // VEVO Info (visa document scan) — cached across visits, scanned in the background
  const [vevoOpen, setVevoOpen] = useState(false);
  const [vevoStatuses, setVevoStatuses] = useState(() => peekVevoStatusMap() ?? {}); // recordId -> { visaType, visaExpiry, dateGranted, dob, scanStatus }
  const [vevoLoading, setVevoLoading] = useState(false);

  const isPastDate = (v) => {
    const m = (v ?? "").toString().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(+m[1], +m[2] - 1, +m[3]) < today;
  };

  // Badge scope matches the modals' default chips: Working AND Active only
  const inBadgeScope = (r) => r.working === "Working" && r.current === "Active";

  const expiredTotal = useMemo(
    () =>
      records.reduce(
        (n, r) =>
          inBadgeScope(r)
            ? n +
              (isPastDate(r.policeExpiry) ? 1 : 0) +
              (isPastDate(r.visaExpiry) ? 1 : 0) +
              (isPastDate(r.workChildrenExpiry) ? 1 : 0)
            : n,
        0
      ),
    [records]
  );

  // Refuse TFN doesn't apply to Employees / Companies — don't count it for them
  // (mirrors the exclusion in ContractExpiryModal so the badge matches the modal)
  const refuseTfnApplies = (r) =>
    !["employee", "company"].includes(
      (r.typeOfEmployment ?? "").toString().trim().toLowerCase()
    );

  const contractExpiredTotal = useMemo(
    () =>
      records.reduce(
        (n, r) =>
          inBadgeScope(r)
            ? n +
              (isPastDate(r.contractExpiry) ? 1 : 0) +
              (refuseTfnApplies(r) && isPastDate(r.refuseTfnExpiry) ? 1 : 0)
            : n,
        0
      ),
    [records]
  );

  // Red badge on the ABN icon = Working + Active contractors needing attention:
  // Cancelled ABNs + Errors (invalid ABN / lookup error) — matches the modal's
  // Cancelled and Error buttons. "Active" is fine; not-yet-looked-up and the
  // no-GUID "unavailable" state don't raise an alert.
  const abnAlertTotal = useMemo(
    () =>
      records.reduce((n, r) => {
        if (!inBadgeScope(r) || r.workstatus !== "ABN") return n;
        const st = abnStatuses[r.recordId]?.status;
        if (!st || st === "Active" || st === "unavailable") return n;
        return n + 1; // Cancelled or Error
      }, 0),
    [records, abnStatuses]
  );

  // Look up every ABN contractor's status against the ABR via the cache.
  // `force` re-checks bypass both the page cache and the server's 24h cache.
  const loadAbnStatuses = useCallback(async (recs, force = false) => {
    setAbnLoading(true);
    try {
      setAbnStatuses(await loadAbnStatusMap(recs, force));
    } catch (err) {
      console.error("ABN status fetch failed:", err);
    } finally {
      setAbnLoading(false);
    }
  }, []);

  // ---- VEVO Info (visa document scan) ----
  const VEVO_MONTHS = { january:0, february:1, march:2, april:3, may:4, june:5, july:6,
    august:7, september:8, october:9, november:10, december:11 };
  const parseVevoDate = (v) => {
    const s = (v ?? "").toString().trim();
    if (!s) return null;
    let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
    m = s.match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})/);
    if (m && VEVO_MONTHS[m[2].toLowerCase()] !== undefined) return new Date(+m[3], VEVO_MONTHS[m[2].toLowerCase()], +m[1]);
    return null;
  };

  // Red badge on the VEVO icon = visas expiring within 3 months + already expired.
  const vevoAlertTotal = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const soon = new Date(today); soon.setMonth(soon.getMonth() + 3);
    let n = 0;
    Object.values(vevoStatuses).forEach((s) => {
      if (!s || s.scanStatus === "unknown" || s.scanStatus === "error") return;
      if (s.forcedBucket === "current") return;          // permanent / in effect
      if (s.forcedBucket === "expired") { n += 1; return; }  // ceased visa
      const d = parseVevoDate(s.visaExpiry);
      if (d && (d < today || d <= soon)) n += 1;         // expired or expiring soon
    });
    return n;
  }, [vevoStatuses]);

  // Scan visa documents (backend) via the cache. `force` bypasses both the
  // page cache and the server's 6h cache.
  const loadVevoStatuses = useCallback(async (force = false) => {
    setVevoLoading(true);
    try {
      setVevoStatuses(await loadVevoStatusMap(force));
    } catch (err) {
      console.error("VEVO scan failed:", err);
    } finally {
      setVevoLoading(false);
    }
  }, []);

  const handleSelectFromExpiry = (record) => {
    setExpiryOpen(false);
    setContractExpiryOpen(false);
    setSearchQuery(record.name || "");
    setSelectedName(record.name || "");
    setShowSuggestions(false);
    setWorkingFilter(record.working === "Working" ? "Working" : "Not Working");
    setRoleFilter(record.workstatus || null);   // resolves the exact ABN/TFN record
    setActivePanel("contract");                  // land straight on Contract Data
  };

  // ABN modal select — same as above but leaves BOTH panels closed: only the
  // main info card + Working/Not Working buttons update to the chosen record.
  const handleSelectFromAbn = (record) => {
    setAbnOpen(false);
    setSearchQuery(record.name || "");
    setSelectedName(record.name || "");
    setShowSuggestions(false);
    setWorkingFilter(record.working === "Working" ? "Working" : "Not Working");
    setRoleFilter(record.workstatus || null);
    setActivePanel(null);                        // keep Info Data & Contract Data closed
  };

  // VEVO detail modal "Manage User" → select the record, close both modals,
  // panels closed (she deep-dives manually) — mirrors the ABN behaviour.
  const handleSelectFromVevo = (record) => {
    setVevoOpen(false);
    setSearchQuery(record.name || "");
    setSelectedName(record.name || "");
    setShowSuggestions(false);
    setWorkingFilter(record.working === "Working" ? "Working" : "Not Working");
    setRoleFilter(record.workstatus || null);
    setActivePanel(null);
  };

  // VEVO "Unknown" row → open the record in Contract Info (to view the document).
  const handleSelectFromVevoContract = (record) => {
    setVevoOpen(false);
    setSearchQuery(record.name || "");
    setSelectedName(record.name || "");
    setShowSuggestions(false);
    setWorkingFilter(record.working === "Working" ? "Working" : "Not Working");
    setRoleFilter(record.workstatus || null);
    setActivePanel("contract");                  // land on Contract Data
  };

  // 3 wrong PINs on the VEVO detail gate → server cleared the session → log out.
  const handleVevoLoggedOut = () => {
    window.location.href = "/index";
  };

  // Friendly ABN status for the info card (mirrors the modal's bucket labels).
  const abnStatusDisplay = (record) => {
    if (!record || record.workstatus !== "ABN") return "N/A";
    const s = abnStatuses[record.recordId];
    if (!s || !s.status || s.status === "unavailable") return abnLoading ? "Checking…" : "—";
    if (s.status === "Active") return s.gst ? "Current + GST" : "Current";
    if (s.status === "Cancelled") return "Cancelled";
    return "Error";
  };

  /* ---------- stylesheet ---------- */
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = personalStyles;
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  /* ---------- toast auto-dismiss ---------- */
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  /* ---------- busy-field mirror (read inside async callbacks) ---------- */
  const busyRef = useRef(busyFields);
  useEffect(() => { busyRef.current = busyFields; }, [busyFields]);

  /* ---------- initial fetch: paint from cache, then revalidate ---------- */
  useEffect(() => {
    let alive = true;
    const warm = !!peekManagedUsers();          // seeded from cache already?
    (async () => {
      try {
        // warm → force a refetch behind the already-painted page
        // cold → this is the real load (loading card is showing)
        const recs = await loadManagedUsers(warm);
        if (!alive) return;
        // don't stomp on a save that's mid-flight with pre-edit server data
        if (warm && busyRef.current.size > 0) return;
        setRecords(recs);
      } catch (err) {
        if (alive && !warm) setLoadError(err.message || "Failed to load users");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  /* ---------- mirror page edits (auto-save, uploads) back into the cache ---------- */
  useEffect(() => {
    if (records.length > 0) syncManagedUsers(records);
  }, [records]);

  /* ---------- ABN statuses: background lookup once users are loaded (compliance only) ---------- */
  const abnFetchedRef = useRef(false);
  useEffect(() => {
    if (isCompliance && !abnFetchedRef.current && records.length > 0) {
      abnFetchedRef.current = true;
      loadAbnStatuses(records);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records, isCompliance]);

  /* ---------- VEVO: background visa-document scan once users are loaded (compliance only) ---------- */
  const vevoFetchedRef = useRef(false);
  useEffect(() => {
    if (isCompliance && !vevoFetchedRef.current && records.length > 0) {
      vevoFetchedRef.current = true;
      loadVevoStatuses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records, isCompliance]);

  /* ---------- close suggestions on outside click ---------- */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ---------- derived ---------- */
  const uniqueNames = useMemo(() => {
    const seen = new Map();
    records.forEach((r) => {
      if (!r.name) return;
      const norm = normalizeName(r.name);
      if (!seen.has(norm)) seen.set(norm, r.name);
    });
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
  }, [records]);

  const suggestions = useMemo(() => {
    const q = normalizeName(searchQuery);
    if (!q) return [];
    return uniqueNames.filter((n) => normalizeName(n).includes(q)).slice(0, 8);
  }, [searchQuery, uniqueNames]);

  const userRecords = useMemo(() => {
    if (!selectedName) return [];
    const target = normalizeName(selectedName);
    return records.filter((r) => normalizeName(r.name) === target);
  }, [selectedName, records]);

  const workingCounts = useMemo(() => {
    const counts = { Working: 0, "Not Working": 0 };
    userRecords.forEach((r) => {
      if (r.working === "Working") counts.Working += 1;
      else counts["Not Working"] += 1;
    });
    return counts;
  }, [userRecords]);

  const recordsForWorking = useMemo(
    () =>
      userRecords.filter((r) =>
        workingFilter === "Working" ? r.working === "Working" : r.working !== "Working"
      ),
    [userRecords, workingFilter]
  );

  const showRoleToggle = recordsForWorking.length >= 2;

  const activeRecord = useMemo(() => {
    if (recordsForWorking.length === 0) return null;
    if (!showRoleToggle) return recordsForWorking[0];
    return (
      recordsForWorking.find((r) => r.workstatus === roleFilter) ??
      recordsForWorking[0]
    );
  }, [recordsForWorking, showRoleToggle, roleFilter]);

  const activeRole = activeRecord?.workstatus ?? null;

  /* ---------- selection / navigation ---------- */
  const handleSearchChange = (val) => {
    setSearchQuery(val);
    setShowSuggestions(true);
    if (selectedName && normalizeName(val) !== normalizeName(selectedName)) {
      setSelectedName(null);
      setActivePanel(null);
      setRoleFilter(null);
    }
  };

  const handleSelectSuggestion = (name) => {
    setSearchQuery(name);
    setSelectedName(name);
    setShowSuggestions(false);
    setActivePanel(null);
    setRoleFilter(null);

    const target = normalizeName(name);
    const recs = records.filter((r) => normalizeName(r.name) === target);
    const hasWorking = recs.some((r) => r.working === "Working");
    setWorkingFilter(hasWorking ? "Working" : "Not Working");
  };

  const handleSwitchWorking = (status) => {
    if (status === workingFilter) return;
    setWorkingFilter(status);
    setRoleFilter(null);
  };

  const handleTogglePanel = (panel) => {
    setActivePanel((prevPanel) => (prevPanel === panel ? null : panel));
  };

  /* ---------- generic auto-save ---------- */
  const isBusy = (recordId, field) => busyFields.has(`${recordId}:${field}`);

  const markBusy = (key, on) =>
    setBusyFields((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });

  const handleFieldSave = async (record, field, value) => {
    const key = `${record.recordId}:${field}`;
    if (busyFields.has(key)) return;
    value = (value ?? "").toString().trim();
    if ((record[field] ?? "") === value) return;

    const oldValue = record[field];

    setRecords((prev) =>
      prev.map((r) => (r.recordId === record.recordId ? { ...r, [field]: value } : r))
    );
    markBusy(key, true);

    try {
      const res = await updateManagedUser(record.recordId, { [field]: value });
      if (res.status === "success") {
        setToast({ type: "success", message: `${labelFor(field)} has been saved.` });
      } else {
        throw new Error(res.error || "Update failed");
      }
    } catch (err) {
      console.error("Update failed:", err);
      setRecords((prev) =>
        prev.map((r) =>
          r.recordId === record.recordId ? { ...r, [field]: oldValue } : r
        )
      );
      setToast({
        type: "error",
        message: err.message || `Failed to save ${labelFor(field)}. Please try again.`,
      });
    } finally {
      markBusy(key, false);
    }
  };

  const handleValidationError = (message) =>
    setToast({ type: "error", message });

  /* ---------- attachment append ---------- */
  const handleAddFile = async (record, field, file) => {
    const key = `${record.recordId}:${field}`;
    if (busyFields.has(key)) return;
    markBusy(key, true);

    try {
      const res = await uploadUserAttachment(record.recordId, field, file);
      if (res.status === "success") {
        setRecords((prev) =>
          prev.map((r) =>
            r.recordId === record.recordId
              ? { ...r, [field]: JSON.stringify(res.items ?? []) }
              : r
          )
        );
        const count = (res.items ?? []).length;
        setToast({
          type: "success",
          message: `${labelFor(field)} has been saved — ${count} file${count === 1 ? "" : "s"} on record.`,
        });
      } else {
        throw new Error(res.error || "Upload failed");
      }
    } catch (err) {
      console.error("Upload failed:", err);
      setToast({
        type: "error",
        message: err.message || `Failed to add file to ${labelFor(field)}.`,
      });
    } finally {
      markBusy(key, false);
    }
  };

  /* ---------- render helpers ---------- */
  const renderField = (record, field) => {
    // Read-only status fields
    if (READONLY_SET.has(field)) {
      const raw = record[field] ?? "";
      const display = READONLY_DATE_DISPLAY.has(field) ? toDisplayDate(raw) : raw;
      return (
        <div className="ob-field" key={`${record.recordId}:${field}`}>
          <label className="ob-label">{labelFor(field)}</label>
          <input
            type="text"
            value={display}
            readOnly
            className="ob-input ob-input-readonly"
          />
        </div>
      );
    }
    // Dropdown fields
    if (SELECT_OPTIONS[field]) {
      return (
        <SavingWrap key={`${record.recordId}:${field}`} saving={isBusy(record.recordId, field)}>
          <CustomSelect
            label={labelFor(field)}
            value={record[field] ?? ""}
            options={SELECT_OPTIONS[field]}
            onChange={(val) => handleFieldSave(record, field, val)}
          />
        </SavingWrap>
      );
    }
    // Editable text / date
    return (
      <EditableText
        key={`${record.recordId}:${field}`}
        record={record}
        field={field}
        saving={isBusy(record.recordId, field)}
        onSave={handleFieldSave}
        onValidationError={handleValidationError}
      />
    );
  };

  /* ---------- render ---------- */
  return (
    <div className="ob-container" style={{ width: "100%", maxWidth: "100%" }}>
      {toast && (
        <div
          className={`ob-toast ${
            toast.type === "success" ? "ob-toast-success" : "ob-toast-error"
          }`}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {toast.type === "success" ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            )}
          </svg>
          {toast.message}
        </div>
      )}

      {/* ================= Compliance modals (compliance variant only) ================= */}
      {isCompliance && (
        <>
          {expiryOpen && (
            <ComplianceExpiryModal
              records={records}
              onSelect={handleSelectFromExpiry}
              onClose={() => setExpiryOpen(false)}
            />
          )}

          {contractExpiryOpen && (
            <ContractExpiryModal
              records={records}
              onSelect={handleSelectFromExpiry}
              onClose={() => setContractExpiryOpen(false)}
            />
          )}

          {abnOpen && (
            <ABNLookup
              records={records}
              statuses={abnStatuses}
              loading={abnLoading}
              onRefresh={() => loadAbnStatuses(records, true)}
              onSelect={handleSelectFromAbn}
              onClose={() => setAbnOpen(false)}
            />
          )}

          {vevoOpen && (
            <VEVOInfo
              records={records}
              statuses={vevoStatuses}
              loading={vevoLoading}
              onRefresh={() => loadVevoStatuses(true)}
              onSelect={handleSelectFromVevo}
              onSelectContract={handleSelectFromVevoContract}
              onLoggedOut={handleVevoLoggedOut}
              onClose={() => setVevoOpen(false)}
            />
          )}
        </>
      )}

      {loading && (
        <div className="ob-form-card">
          <div className="ob-info-banner">
            <LoadingState message={"Loading users…"} />
          </div>
        </div>
      )}

      {!loading && loadError && (
        <div className="ob-form-card">
          <div className="ob-info-banner ob-info-banner-warning">
            <ErrorIcon /> {loadError}
          </div>
        </div>
      )}

      {!loading && !loadError && (
        <>
          {/* ================= Search card ================= */}
          <div
            className="ob-form-card"
            style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box", maxHeight: "none" }}
          >
            <div className="ob-record-card">
              <div className="ob-record-header">
                <div className="ob-record-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"
                    />
                  </svg>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <h3 className="ob-record-title" style={{ margin: 0 }}>
                    {meta.title}
                  </h3>
                  <HelpButton helpData={[meta.help]} />

                  {/* basic: Excel downloads */}
                  {!isCompliance && (
                    <>
                      <DownloadIconButton
                        title="Contractor Download"
                        filename={`contractors_${new Date().toISOString().slice(0, 10)}.xlsx`}
                        fetchFile={downloadUsersManage_}
                        arg="ABN"
                        setToast={setToast}
                      />
                      <DownloadIconButton
                        title="Employee Download"
                        filename={`employees_${new Date().toISOString().slice(0, 10)}.xlsx`}
                        fetchFile={downloadUsersManage_}
                        arg="TFN"
                        setToast={setToast}
                      />
                    </>
                  )}

                  {/* compliance: the four alert icons */}
                  {isCompliance && (
                    <>
                      <ClockIconButton onClick={() => setExpiryOpen(true)} alertCount={expiredTotal} />
                      <ClockIconButton
                        onClick={() => setContractExpiryOpen(true)}
                        alertCount={contractExpiredTotal}
                        title="Contract Expiry's"
                        icon={<ContractClockIcon />}
                      />
                      <ClockIconButton
                        onClick={() => setAbnOpen(true)}
                        alertCount={abnAlertTotal}
                        title="ABN Status"
                        icon={<AbnIcon />}
                      />
                      <ClockIconButton
                        onClick={() => setVevoOpen(true)}
                        alertCount={vevoAlertTotal}
                        title="VEVO Info"
                        icon={<VevoIcon />}
                      />
                    </>
                  )}
                </div>
              </div>
              <p className="ob-helper" style={{ marginTop: "8px" }}>
                {meta.helper}
              </p>
            </div>

            <div ref={searchWrapperRef} style={{ position: "relative", marginTop: "20px" }}>
              <SearchInput
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search by name..."
              />

              {showSuggestions && suggestions.length > 0 && (
                <ul
                  style={{
                    position: "absolute",
                    top: "calc(100% + 4px)",
                    left: 0,
                    right: 0,
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    listStyle: "none",
                    margin: 0,
                    padding: "4px 0",
                    maxHeight: "240px",
                    overflowY: "auto",
                    zIndex: 10,
                  }}
                >
                  {suggestions.map((name) => (
                    <li
                      key={name}
                      onClick={() => handleSelectSuggestion(name)}
                      style={{ padding: "10px 14px", cursor: "pointer", fontSize: "14px", color: "#111827" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f4f6")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      {name}
                    </li>
                  ))}
                </ul>
              )}

              {showSuggestions && searchQuery.trim() && suggestions.length === 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: "calc(100% + 4px)",
                    left: 0,
                    right: 0,
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    padding: "12px 14px",
                    fontSize: "14px",
                    color: "#6b7280",
                    zIndex: 10,
                  }}
                >
                  No matching users.
                </div>
              )}
            </div>
          </div>

          {/* ================= User Status Info (always visible) ================= */}
          {selectedName && userRecords.length > 0 && (
            <div
              className="ob-form-card"
              style={{ marginTop: "20px", width: "100%", maxWidth: "100%", boxSizing: "border-box", maxHeight: "none" }}
            >
              {/* Working / Not Working toggle */}
              <div
                className="ob-form-fields"
                style={{ display: "flex", gap: "16px", flexDirection: "row" }}
              >
                {WORKING_OPTIONS.map((status) => (
                  <button
                    key={status}
                    onClick={() => handleSwitchWorking(status)}
                    disabled={workingCounts[status] === 0}
                    className={`ob-btn ${
                      workingFilter === status ? "ob-btn-submit" : "ob-btn-check"
                    }`}
                    style={{ flex: 1, opacity: workingCounts[status] === 0 ? 0.4 : 1 }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <span style={{ fontSize: "16px", fontWeight: 600 }}>{status}</span>
                      <span style={{ fontSize: "12px", opacity: 0.85, marginTop: "2px" }}>
                        {workingCounts[status]} record{workingCounts[status] === 1 ? "" : "s"}
                      </span>
                    </div>
                  </button>
                ))}
              </div>

              {/* Contractor / Employee toggle — only when 2x same working status */}
              {showRoleToggle && (
                <div
                  className="ob-form-fields"
                  style={{ display: "flex", gap: "16px", flexDirection: "row", marginTop: "12px" }}
                >
                  {recordsForWorking.map((r) => (
                    <button
                      key={r.recordId}
                      onClick={() => setRoleFilter(r.workstatus)}
                      className={`ob-btn ${
                        activeRole === r.workstatus ? "ob-btn-submit" : "ob-btn-check"
                      }`}
                      style={{ flex: 1 }}
                    >
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <span style={{ fontSize: "16px", fontWeight: 600 }}>
                          {statusDisplay(r.workstatus)}
                        </span>
                        <span style={{ fontSize: "12px", opacity: 0.85, marginTop: "2px" }}>
                          {workingFilter.toLowerCase()} record
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {activeRecord ? (
                <div
                  style={{
                    marginTop: "16px",
                    background: "rgba(255, 255, 255, 0.03)",
                    border: "1px solid rgba(34, 211, 238, 0.15)",
                    borderRadius: "12px",
                    padding: "14px",
                    display: "grid",
                    gridTemplateColumns: "auto minmax(0, 1fr) minmax(0, 1fr)",
                    gridTemplateRows: "auto auto auto auto",
                    columnGap: "14px",
                    rowGap: "10px",
                    minWidth: 0,
                  }}
                >
                  {/* Photo — full left column, read-only */}
                  <div style={{ gridColumn: "1 / 2", gridRow: "1 / 5" }}>
                    {activeRecord.photo ? (
                      <img
                        src={activeRecord.photo}
                        alt={activeRecord.name || "User photo"}
                        style={{
                          width: "260px",
                          height: "100%",
                          minHeight: "150px",
                          objectFit: "cover",
                          borderRadius: "12px",
                          border: "1px solid rgba(34, 211, 238, 0.25)",
                          display: "block",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "130px",
                          height: "100%",
                          minHeight: "150px",
                          borderRadius: "12px",
                          border: "1px dashed rgba(34, 211, 238, 0.25)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "rgba(255,255,255,0.3)",
                          fontSize: "0.7rem",
                          fontStyle: "italic",
                        }}
                      >
                        No photo
                      </div>
                    )}
                  </div>

                  <div style={{ gridColumn: "2 / -1", minWidth: 0 }}>
                    {renderField(activeRecord, "businessName")}
                  </div>

                  {/* Name — read-only. On the basic page it takes the full row,
                      on the compliance page it shares the row with ABN Status */}
                  <div
                    className="ob-field"
                    style={{ gridColumn: isCompliance ? "2 / 3" : "2 / -1", minWidth: 0 }}
                  >
                    <label className="ob-label">Name</label>
                    <input
                      type="text"
                      value={activeRecord.name || ""}
                      readOnly
                      className="ob-input ob-input-readonly"
                      title={activeRecord.name || ""}
                    />
                  </div>

                  {/* ABN Status — read-only, compliance page only (reads the ABR lookup) */}
                  {isCompliance && (
                    <div className="ob-field" style={{ gridColumn: "3 / -1", minWidth: 0 }}>
                      <label className="ob-label">ABN Status</label>
                      <input
                        type="text"
                        value={abnStatusDisplay(activeRecord)}
                        readOnly
                        className="ob-input ob-input-readonly"
                      />
                    </div>
                  )}

                  {/* Row 3 — Email (editable) | Status (read-only, substituted) */}
                  <div style={{ minWidth: 0 }}>{renderField(activeRecord, "email")}</div>
                  <div className="ob-field" style={{ minWidth: 0 }}>
                    <label className="ob-label">{FIELD_LABELS.workstatus}</label>
                    <input
                      type="text"
                      value={statusDisplay(activeRecord.workstatus)}
                      readOnly
                      className="ob-input ob-input-readonly"
                    />
                  </div>

                  {/* Row 4 — Current | Working (editable selects) */}
                  <div style={{ minWidth: 0 }}>{renderField(activeRecord, "current")}</div>
                  <div style={{ minWidth: 0 }}>{renderField(activeRecord, "working")}</div>
                </div>
              ) : (
                <div
                  style={{
                    marginTop: "16px",
                    padding: "24px",
                    textAlign: "center",
                    color: "rgba(255,255,255,0.5)",
                    fontStyle: "italic",
                  }}
                >
                  No {workingFilter.toLowerCase()} records for {selectedName}.
                </div>
              )}

              {/* Info Data / Contract Data — one open at a time */}
              {activeRecord && (
                <div
                  className="ob-form-fields"
                  style={{ display: "flex", gap: "16px", flexDirection: "row", marginTop: "20px" }}
                >
                  {[
                    { key: "info", label: "Info Data", hint: "personal & banking details" },
                    { key: "contract", label: "Contract Data", hint: "documents & contracts" },
                  ].map((panel) => (
                    <button
                      key={panel.key}
                      onClick={() => handleTogglePanel(panel.key)}
                      className={`ob-btn ${
                        activePanel === panel.key ? "ob-btn-submit" : "ob-btn-check"
                      }`}
                      style={{ flex: 1 }}
                    >
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <span style={{ fontSize: "16px", fontWeight: 600 }}>{panel.label}</span>
                        <span style={{ fontSize: "12px", opacity: 0.85, marginTop: "2px" }}>
                          {activePanel === panel.key ? "click to close" : panel.hint}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ================= Info Data panel ================= */}
          {activeRecord && activePanel === "info" && (
            <div
              className="ob-form-card"
              style={{ marginTop: "20px", width: "100%", maxWidth: "100%", boxSizing: "border-box", maxHeight: "none" }}
            >
              <div className="ob-form-fields">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <h2 className="ob-helper" style={{ fontSize: "20px", margin: 0, fontWeight: 600 }}>
                    {statusDisplay(activeRole)} Info Data
                  </h2>
                  <button
                    onClick={() => setActivePanel(null)}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", fontSize: "14px" }}
                  >
                    Close
                  </button>
                </div>

                {/* Editable fields */}
                <div style={THREE_COL_GRID}>
                  {infoFieldOrder(activeRecord.workstatus).map((field) =>
                    renderField(activeRecord, field)
                  )}
                </div>

                {/* Read-only status fields */}
                <div className="ob-divider" />
                <div
                  style={{
                    color: "rgba(34, 211, 238, 0.7)",
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    marginBottom: "4px",
                  }}
                >
                  Status (read-only)
                </div>
                <div style={THREE_COL_GRID}>
                  {READONLY_INFO_FIELDS.map((field) => renderField(activeRecord, field))}
                </div>

                {/* Banking */}
                <div className="ob-divider" />
                <div style={{ color: "#991b1b", fontSize: "14px", fontWeight: 400, marginBottom: "8px" }}>
                  Banking details are sensitive — changes save immediately.
                </div>
                <div style={THREE_COL_GRID}>
                  {BANKING_FIELD_ORDER.map((field) => renderField(activeRecord, field))}
                </div>
              </div>
            </div>
          )}

          {/* ================= Contract Data panel ================= */}
          {activeRecord && activePanel === "contract" && (
            <div
              className="ob-form-card"
              style={{ marginTop: "20px", width: "100%", maxWidth: "100%", boxSizing: "border-box", maxHeight: "none" }}
            >
              <div className="ob-form-fields">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <h2 className="ob-helper" style={{ fontSize: "20px", margin: 0, fontWeight: 600 }}>
                    {statusDisplay(activeRole)} Contract Data
                  </h2>
                  <button
                    onClick={() => setActivePanel(null)}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", fontSize: "14px" }}
                  >
                    Close
                  </button>
                </div>

                <div style={THREE_COL_GRID}>
                  {CONTRACT_SCALAR_ORDER.map((field) => renderField(activeRecord, field))}
                </div>

                <div className="ob-divider" />
                <div
                  style={{
                    color: "rgba(34, 211, 238, 0.7)",
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    marginBottom: "4px",
                  }}
                >
                  Documents — Add appends a PDF to the existing files
                </div>

                <div style={THREE_COL_GRID}>
                  {ATTACHMENT_FIELD_ORDER.map((field) => (
                    <AttachmentCarousel
                      key={`${activeRecord.recordId}:${field}`}
                      record={activeRecord}
                      field={field}
                      busy={isBusy(activeRecord.recordId, field)}
                      onAddFile={handleAddFile}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* Compliance page — same component, the four alert icons instead of downloads */
export const UsersCompliance = () => <UsersManagement variant="compliance" />;

export default UsersManagement;